import React from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { getAuth, signOut } from "firebase/auth";
import { useUserContext } from "../../contexts/UserContext";

function NavItem({ to, children }: { to: string; children: React.ReactNode }) {
    return (
        <NavLink
            to={to}
            className={({ isActive }) =>
                [
                    "px-2 py-1 rounded border text-xs whitespace-nowrap",
                    "hover:bg-gray-50",
                    isActive
                        ? "text-blue-700 border-blue-200 bg-blue-50 font-semibold"
                        : "text-gray-700 border-transparent",
                ].join(" ")
            }
        >
            {children}
        </NavLink>
    );
}

export default function SuperAdminLayout() {
    const navigate = useNavigate();
    const userState = useUserContext();

    // allTenants:true の神だけ表示
    const canManageSuperUsers = userState.superAdminGlobal === true;

    const logout = async () => {

        console.log("★ SuperAdminLayout: logout");

        // ✅ ログアウトログ記録
        const selectedTenant = localStorage.getItem("selectedTenant") || "ecvision";
        const uid = userState.uid;
        const email = getAuth().currentUser?.email ?? null;

        fetch("/api/auth/logs", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                event_type: "logout",
                tenantId: selectedTenant,
                uid: uid || null,
                email: email || null,
                provider: "firebase",
            }),
        }).catch((e) => {
            console.warn("[auth/logs] failed to log logout:", e?.message);
        });

        localStorage.removeItem("ecx_access_token");
        localStorage.removeItem("ecx_token_expiry");
        localStorage.removeItem("selectedTenant");
        sessionStorage.removeItem("login_tenant");
        sessionStorage.removeItem("ecx_oauth_state");
        sessionStorage.removeItem("ecx_code_verifier");
        localStorage.removeItem("postLoginRedirect");

        await signOut(getAuth());
        navigate("/login");
    };

    return (
        <div className="h-dvh bg-gray-50 flex flex-col">
            <header className="w-full bg-white border-b sticky top-0 z-50">
                <div className="w-full px-2 h-12 flex items-center justify-between gap-2">
                    {/* nav: 横スクロールで高さを増やさない */}
                    <nav className="flex items-center gap-1 overflow-x-auto no-scrollbar">
                        <NavItem to="/super/companies">会社一覧</NavItem>
                        <NavItem to="/super/companies/monthly-metrics">前月売上/広告費</NavItem>
                        <NavItem to="/super/users">ユーザー管理</NavItem>
                        <NavItem to="/super/notifications">お知らせ管理</NavItem>
                        <NavItem to="/super/rakuten-events">楽天イベント</NavItem>
                        <NavItem to="/super/chatwork-senders">Chatwork投稿者</NavItem>
                        <NavItem to="/super/sync-health">同期ヘルス</NavItem>
                        <NavItem to="/super/scheduler-runs">実行履歴</NavItem>
                        <NavItem to="/super/rms-billing">RMS課金確認</NavItem>
                        <NavItem to="/super/auth-logs">ログイン履歴</NavItem>

                        {canManageSuperUsers && <NavItem to="/super/rms-download">RMS手動DL</NavItem>}
                        {canManageSuperUsers && <NavItem to="/super/superusers">スーパーユーザー</NavItem>}
                    </nav>

                    <div className="flex items-center gap-2 shrink-0">
                        <button
                            onClick={logout}
                            className="text-xs px-2 py-1 rounded border border-red-200 text-red-700 bg-white hover:bg-red-50"
                            title="ログアウト"
                        >
                            ログアウト
                        </button>
                    </div>
                </div>
            </header>

            {/* main: 管理画面は基本コンパクト、子でスクロールさせる */}
            <main className="w-full max-w-none p-2 flex-1 min-h-0 overflow-hidden">
                <Outlet />
            </main>
        </div>
    );
}

/**
 * Tailwind に no-scrollbar が無い場合は globals.css に追加（任意）
 *
 * .no-scrollbar::-webkit-scrollbar { display: none; }
 * .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
 */
